import { useAtomValue } from "jotai";
import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { plusPopupAtom } from "scmp-app/components/plus/atoms";
import type { QueueNames } from "scmp-app/components/plus/detailed-listing-popup/detailed-listing-queue/data";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { featuredOpinionBlockContainerQuery } from "scmp-app/queries/__generated__/featuredOpinionBlockContainerQuery.graphql";

import { FeaturedOpinionBlockItem } from "./featured-opinion-block-item";
import { ContentContainer, Heading } from "./styles";

export type Props = {
  className?: string;
  excludeEntityIds: string[];
  queueName: QueueNames;
};

type Settings = {
  custom_text?: string;
};

export const FeaturedOpinionBlockContainer: FunctionComponent<Props> = ({
  excludeEntityIds,
  queueName,
}) => {
  const data = useLazyLoadQuery<featuredOpinionBlockContainerQuery>(
    graphql`
      query featuredOpinionBlockContainerQuery($queueName: String!, $excludeEntityIds: [String!]!) {
        queue(filter: { name: $queueName }) {
          settings
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Article {
                  entityId
                  ...featuredOpinionBlockItemContent
                }
              }
            }
          }
        }
      }
    `,
    {
      excludeEntityIds,
      queueName,
    },
    { fetchPolicy: "store-or-network" },
  );

  const contents = useMemo(
    () => data?.queue?.items?.edges?.map(edge => edge.node) ?? [],
    [data?.queue?.items?.edges],
  );
  const ga4ImpressionEvent = useCallback(
    () =>
      ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          list_of_articles: contents.map?.(item => item.entityId).join(",") ?? "",
          page_type: "homepage",
          widget_name: "featured_opinion",
        },
        subcategory: "widget",
      }) satisfies RecirculationWidgetImpressionEvent,
    [contents],
  );

  const { isShowArticlePopup: shouldDisableImpressionTracking } = useAtomValue(plusPopupAtom);

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: {
        isSendGA4Tracking: true,
        shouldDisable: shouldDisableImpressionTracking,
        shouldSendOnce: true,
      },
    });

  const customText = (data?.queue?.settings as Settings)?.custom_text;
  const displayName = customText ?? "";

  return (
    <>
      <Heading>{displayName}</Heading>
      <ContentContainer ref={captureTrackImpressionEventTargetElement}>
        {contents.map(
          item =>
            item?.entityId && <FeaturedOpinionBlockItem key={item.entityId} reference={item} />,
        )}
      </ContentContainer>
    </>
  );
};

FeaturedOpinionBlockContainer.displayName = "FeaturedOpinionBlockContainer";
